import { supabase } from '../lib/supabase';

export const messagingService = {
  /**
   * Get all conversations for the current user with full-text search support
   */
  async getConversations(userId, searchQuery = '') {
    try {
      // Validate supabase client
      if (!supabase) {
        throw new Error('Supabase client not initialized. Please check your configuration.');
      }

      // Validate userId
      if (!userId) {
        throw new Error('User ID is required to fetch conversations');
      }

      let query = supabase?.from('conversations')?.select(`
          id,
          user1_id,
          user2_id,
          listing_id,
          status,
          last_message_at,
          created_at,
          user1:user1_id(id, full_name, avatar_url),
          user2:user2_id(id, full_name, avatar_url),
          listing:listing_id(id, title, category),
          messages(id, content, created_at, is_read, sender_id)
        `)?.or(`user1_id.eq.${userId},user2_id.eq.${userId}`)?.eq('status', 'active')?.order('last_message_at', { ascending: false });

      const { data: conversations, error } = await query;

      if (error) {
        console.error('Supabase query error:', error);
        throw new Error(`Database error: ${error.message || 'Failed to fetch conversations'}`);
      }

      // Handle empty results gracefully
      if (!conversations || conversations?.length === 0) {
        return [];
      }

      // Process conversations to get contact info and last message
      const processedConversations = conversations?.map(conv => {
        // Determine contact (the other user)
        const isUser1 = conv?.user1_id === userId;
        const contact = isUser1 ? conv?.user2 : conv?.user1;
        
        // Get last message
        const sortedMessages = (conv?.messages || [])?.sort(
          (a, b) => new Date(b?.created_at || 0) - new Date(a?.created_at || 0)
        );
        const lastMessage = sortedMessages?.[0];
        
        // Count unread messages
        const unreadCount = (conv?.messages || [])?.filter(
          msg => msg?.sender_id !== userId && !msg?.is_read
        )?.length;

        return {
          id: conv?.id,
          contactId: contact?.id || null,
          contactName: contact?.full_name || 'Unknown User',
          contactAvatar: contact?.avatar_url || '',
          listingId: conv?.listing_id,
          listingTitle: conv?.listing?.title || '',
          listingCategory: conv?.listing?.category || '',
          lastMessage: lastMessage?.content || '',
          lastMessageTime: lastMessage?.created_at || conv?.last_message_at,
          unreadCount,
          status: conv?.status,
          createdAt: conv?.created_at
        };
      });

      // If search query provided, filter by message content or contact name
      if (searchQuery?.trim()) {
        const lowerQuery = searchQuery?.toLowerCase();
        return processedConversations?.filter(conv =>
          conv?.contactName?.toLowerCase()?.includes(lowerQuery) ||
          conv?.listingTitle?.toLowerCase()?.includes(lowerQuery) ||
          conv?.lastMessage?.toLowerCase()?.includes(lowerQuery)
        );
      }

      return processedConversations;
    } catch (error) {
      console.error('Error fetching conversations:', error);
      // Re-throw with more context
      throw new Error(error?.message || 'An unexpected error occurred while loading conversations');
    }
  },

  /**
   * Search messages using full-text search
   */
  async searchMessages(userId, searchQuery) {
    try {
      const { data, error } = await supabase?.rpc('search_messages', {
        user_uuid: userId,
        search_query: searchQuery
      });

      if (error) throw error;

      return data?.map(msg => ({
        conversationId: msg?.conversation_id,
        messageId: msg?.message_id,
        senderId: msg?.sender_id,
        content: msg?.content,
        createdAt: msg?.created_at,
        rank: msg?.rank
      }));
    } catch (error) {
      console.error('Error searching messages:', error);
      throw error;
    }
  },

  /**
   * Get messages for a conversation
   */
  async getMessages(conversationId) {
    try {
      const { data, error } = await supabase?.from('messages')?.select(`
          id,
          conversation_id,
          sender_id,
          content,
          is_read,
          created_at,
          sender:sender_id(id, full_name, avatar_url)
        `)?.eq('conversation_id', conversationId)?.order('created_at', { ascending: true });

      if (error) throw error;

      return data?.map(msg => ({
        id: msg?.id,
        conversationId: msg?.conversation_id,
        senderId: msg?.sender_id,
        senderName: msg?.sender?.full_name || 'Unknown',
        senderAvatar: msg?.sender?.avatar_url || '',
        content: msg?.content,
        isRead: msg?.is_read,
        createdAt: msg?.created_at,
        type: msg?.sender_id === conversationId ? 'sent' : 'received'
      }));
    } catch (error) {
      console.error('Error fetching messages:', error);
      throw error;
    }
  },

  /**
   * Send a message
   */
  async sendMessage(conversationId, userId, content) {
    try {
      const { data, error } = await supabase?.from('messages')?.insert({
          conversation_id: conversationId,
          sender_id: userId,
          content: content,
          is_read: false
        })?.select()?.single();

      if (error) throw error;

      return {
        id: data?.id,
        conversationId: data?.conversation_id,
        senderId: data?.sender_id,
        content: data?.content,
        isRead: data?.is_read,
        createdAt: data?.created_at
      };
    } catch (error) {
      console.error('Error sending message:', error);
      throw error;
    }
  },

  /**
   * Mark messages as read
   */
  async markMessagesAsRead(conversationId, userId) {
    try {
      const { error } = await supabase?.from('messages')?.update({ is_read: true })?.eq('conversation_id', conversationId)?.neq('sender_id', userId)?.eq('is_read', false);

      if (error) throw error;
    } catch (error) {
      console.error('Error marking messages as read:', error);
      throw error;
    }
  },

  /**
   * Create a new conversation
   */
  async createConversation(userId, contactId, listingId = null) {
    try {
      // Check if conversation already exists
      const { data: existing } = await supabase?.from('conversations')?.select('id')?.or(`and(user1_id.eq.${userId},user2_id.eq.${contactId}),and(user1_id.eq.${contactId},user2_id.eq.${userId})`)?.single();

      if (existing) {
        return existing?.id;
      }

      // Create new conversation
      const { data, error } = await supabase?.from('conversations')?.insert({
          user1_id: userId,
          user2_id: contactId,
          listing_id: listingId,
          status: 'active'
        })?.select()?.single();

      if (error) throw error;

      return data?.id;
    } catch (error) {
      console.error('Error creating conversation:', error);
      throw error;
    }
  },

  /**
   * Subscribe to new messages in a conversation
   */
  subscribeToMessages(conversationId, callback) {
    const channel = supabase?.channel(`messages:${conversationId}`)?.on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'messages',
          filter: `conversation_id=eq.${conversationId}`
        },
        (payload) => callback(payload?.new)
      )?.subscribe();

    return () => supabase?.removeChannel(channel);
  }
};

export async function getConversationMessages(conversationId) {
  try {
    return await messagingService?.getMessages(conversationId);
  } catch (error) {
    console.error('Error fetching conversation messages:', error);
    throw error;
  }
}